import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import { colors } from "../constants/designTokens";
import { parseSiengeFile } from "../services/importacaoService";

const IMPORT_VALIDATION_FLOW = [
  {
    id: "file",
    title: "Recebimento da planilha",
    helper: "Valida extensao permitida, disponibilidade do arquivo e integridade basica.",
  },
  {
    id: "sheet",
    title: "Leitura da estrutura",
    helper: "Abre a primeira aba e mede o volume inicial de linhas para analise.",
  },
  {
    id: "headers",
    title: "Validacao do template",
    helper: "Confere a linha de cabecalho do MAPA/Sienge e os aliases aceitos no layout.",
  },
  {
    id: "rows",
    title: "Tratamento dos registros",
    helper: "Normaliza mes/ano, converte valores numericos e descarta linhas vazias.",
  },
  {
    id: "portal",
    title: "Carga no portal",
    helper: "Consolida o payload e prepara a persistencia dos dados importados no portal.",
  },
];

function createImportFlowState() {
  return {
    currentStepId: null,
    errorStepId: null,
    isComplete: false,
    progressValue: 0,
    details: {},
    summary: null,
  };
}

function getImportErrorMessage(error) {
  const responseData = error?.response?.data;
  const apiMessage = responseData?.mensagem || responseData?.message;
  const apiErrors = Array.isArray(responseData?.erros)
    ? responseData.erros.filter(Boolean)
    : [];

  if (apiMessage && apiErrors.length) {
    return `${apiMessage} ${apiErrors.join("; ")}`;
  }

  if (apiMessage) {
    return apiMessage;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Falha ao interpretar o arquivo. Verifique o template do relatorio Sienge.";
}

export default function UploadDropzone({ onImportComplete, autoOpenKey = 0, compact = false }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [status, setStatus] = useState("idle");
  const [fileName, setFileName] = useState("");
  const [message, setMessage] = useState("");
  const [duplicateMessage, setDuplicateMessage] = useState("");
  const [importFlow, setImportFlow] = useState(() => createImportFlowState());

  const stepIds = useMemo(
    () => IMPORT_VALIDATION_FLOW.map((step) => step.id),
    []
  );

  const validationSteps = useMemo(() => {
    const currentIndex = stepIds.indexOf(importFlow.currentStepId);

    return IMPORT_VALIDATION_FLOW.map((step, index) => {
      let stepStatus = "idle";

      if (importFlow.errorStepId === step.id) {
        stepStatus = "error";
      } else if (importFlow.isComplete) {
        stepStatus = "done";
      } else if (currentIndex > index) {
        stepStatus = "done";
      } else if (currentIndex === index) {
        stepStatus = "active";
      }

      return {
        ...step,
        status: stepStatus,
        detail: importFlow.details[step.id] || step.helper,
      };
    });
  }, [importFlow, stepIds]);

  const currentStep = validationSteps.find(
    (step) => step.id === importFlow.currentStepId
  );

  useEffect(() => {
    if (!autoOpenKey || status !== "idle") {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      inputRef.current?.click();
    }, 120);

    return () => clearTimeout(timeoutId);
  }, [autoOpenKey, status]);

  function resetImporterState() {
    setStatus("idle");
    setFileName("");
    setMessage("");
    setDuplicateMessage("");
    setImportFlow(createImportFlowState());
  }

  function handleImportAnotherFile(event) {
    event.stopPropagation();
    if (status === "processing") {
      return;
    }
    resetImporterState();
    inputRef.current?.click();
  }

  function handleImportProgress({ stepId, progress, detail, summary }) {
    setImportFlow((currentFlow) => ({
      ...currentFlow,
      currentStepId: stepId,
      errorStepId: null,
      isComplete: false,
      progressValue: typeof progress === "number" ? progress : currentFlow.progressValue,
      details: detail
        ? {
            ...currentFlow.details,
            [stepId]: detail,
          }
        : currentFlow.details,
      summary: summary
        ? {
            ...(currentFlow.summary || {}),
            ...summary,
          }
        : currentFlow.summary,
    }));
  }

  function handleImportError(errorMessage) {
    setImportFlow((currentFlow) => {
      const fallbackStepId = currentFlow.currentStepId || "file";

      return {
        ...currentFlow,
        errorStepId: fallbackStepId,
        progressValue: currentFlow.progressValue || 12,
        details: {
          ...currentFlow.details,
          [fallbackStepId]: errorMessage,
        },
      };
    });
  }

  async function processFile(file) {
    try {
      setStatus("processing");
      setFileName(file.name);
      setMessage("");
      setDuplicateMessage("");
      setImportFlow(createImportFlowState());

      const parsed = await parseSiengeFile(file, {
        onProgress: handleImportProgress,
      });

      handleImportProgress({
        stepId: "portal",
        progress: 92,
        detail: `${parsed.records.length} registro(s) validados no front. Enviando arquivo original para a API.`,
        summary: {
          sheetName: parsed.sheetName,
          totalRows: parsed.totalRows,
          eligibleRows: parsed.eligibleRows,
        },
      });

      const importStats = await Promise.resolve(
        onImportComplete(file.name, parsed.records, parsed, file)
      );
      const isDuplicateImport = Boolean(importStats?.noChanges);
      const backendResult = importStats?.backendResult;

      const successMessage = isDuplicateImport
        ? importStats.message || "Planilha ja inserida, sem dados para atualizar"
        : backendResult?.mensagem
        ? `${backendResult.mensagem} ${backendResult.totalRegistros ?? parsed.records.length} registros enviados para a API.`
        : importStats
        ? `${importStats.totalRecords} registros carregados no portal (${importStats.newRecords} novos / ${importStats.updatedRecords} atualizados).`
        : `${parsed.records.length} registros processados com sucesso na aba ${parsed.sheetName}.`;

      setStatus("done");
      setMessage(successMessage);
      setDuplicateMessage(isDuplicateImport ? successMessage : "");
      setImportFlow((currentFlow) => ({
        ...currentFlow,
        currentStepId: "portal",
        errorStepId: null,
        isComplete: true,
        progressValue: 100,
        details: {
          ...currentFlow.details,
          portal: successMessage,
        },
        summary: {
          ...(currentFlow.summary || {}),
          sheetName: parsed.sheetName,
          totalRows: parsed.totalRows,
          eligibleRows: parsed.eligibleRows,
          totalRecords: backendResult?.totalRegistros ?? importStats?.totalRecords ?? parsed.records.length,
          newRecords: backendResult?.novosRegistros ?? importStats?.newRecords ?? null,
          updatedRecords: backendResult?.registrosAtualizados ?? importStats?.updatedRecords ?? null,
        },
      }));
    } catch (error) {
      const errorMessage = getImportErrorMessage(error);

      setStatus("error");
      setMessage(errorMessage);
      setDuplicateMessage("");
      handleImportError(errorMessage);
    }
  }

  function handleDrop(event) {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  }

  function handleFileChange(event) {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
    event.target.value = "";
  }

  function renderValidationIndicator(stepStatus) {
    if (stepStatus === "done") {
      return <CheckCircleRounded sx={{ fontSize: 20, color: colors.green }} />;
    }

    if (stepStatus === "error") {
      return <ErrorOutlineRounded sx={{ fontSize: 20, color: colors.red }} />;
    }

    if (stepStatus === "active") {
      return <CircularProgress size={18} thickness={5} />;
    }

    return (
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          bgcolor: "#c7d2e4",
        }}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <Paper
        elevation={0}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => {
          if (status !== "processing") {
            inputRef.current?.click();
          }
        }}
        sx={{
          minHeight: compact ? 96 : 238,
          borderRadius: compact ? 2.2 : 3,
          border: compact
            ? `1px solid ${dragging ? colors.primary : colors.border}`
            : `2px dashed ${dragging ? colors.primary : colors.border}`,
          bgcolor: dragging ? "var(--ac-highlight-blue, #eef4ff)" : compact ? colors.surface : colors.surfaceAlt,
          display: "grid",
          placeItems: "center",
          cursor: "pointer",
          transition: "all 0.25s ease",
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          hidden
          onChange={handleFileChange}
        />

        <Stack alignItems="center" spacing={1.2} sx={{ px: 2, textAlign: "center" }}>
          {status === "processing" ? (
            <CircularProgress size={38} />
          ) : status === "done" ? (
            <CheckCircleRounded sx={{ fontSize: 42, color: colors.green }} />
          ) : status === "error" ? (
            <ErrorOutlineRounded sx={{ fontSize: 42, color: colors.red }} />
          ) : (
            <Box sx={{ width: compact ? 44 : 60, height: compact ? 44 : 60, borderRadius: compact ? 2 : 3, bgcolor: colors.surfaceSoft, color: colors.softMuted, display: "grid", placeItems: "center" }}>
              <UploadFileOutlined sx={{ fontSize: compact ? 24 : 32 }} />
            </Box>
          )}

          <Typography sx={{ color: colors.text, fontSize: compact ? 15 : 18, fontWeight: 900 }}>
            {status === "processing"
              ? "Validando e carregando dados"
              : status === "done"
                ? "Importacao concluida"
                : status === "error"
                  ? "Erro ao importar arquivo"
                  : compact
                    ? "Selecionar arquivo"
                    : "Importar relatorio Sienge"}
          </Typography>

          <Typography sx={{ color: colors.softMuted, fontSize: 13 }}>
            {status === "processing"
              ? currentStep?.detail || fileName
              : status === "done" || status === "error"
                ? message
                : compact
                  ? "CSV, XLSX ou XLS"
                  : "Arraste e solte seu arquivo CSV ou XLSX aqui"}
          </Typography>

          {status === "idle" && !compact ? (
            <Typography sx={{ color: colors.softMuted, fontSize: 12 }}>
              ou clique para selecionar
            </Typography>
          ) : null}

          {status === "done" || status === "error" ? (
            <Button
              variant="outlined"
              onClick={handleImportAnotherFile}
              sx={{ mt: 0.6, textTransform: "none", fontWeight: 700 }}
            >
              Importar outro arquivo
            </Button>
          ) : null}
        </Stack>
      </Paper>

      {status !== "idle" ? (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          bgcolor: colors.surface,
          border: `1px solid ${colors.border}`,
          overflow: "hidden",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={1.2}
          sx={{ px: 2, py: 1.6, borderBottom: `1px solid ${colors.border}` }}
        >
          <Box>
            <Typography sx={{ color: colors.text, fontWeight: 900 }}>
              Validacoes do front-end
            </Typography>
            <Typography sx={{ color: colors.softMuted, fontSize: 12.5, mt: 0.35 }}>
              O portal acompanha cada checagem antes de refletir os dados importados.
            </Typography>
          </Box>
          <Chip
            label={
              status === "processing"
                ? `${Math.round(importFlow.progressValue)}% em andamento`
                : status === "done"
                  ? "Carga concluida"
                  : status === "error"
                    ? "Fluxo interrompido"
                    : "Aguardando arquivo"
            }
            size="small"
            sx={{
              bgcolor:
                status === "done"
                  ? "#d8f9e9"
                  : status === "error"
                    ? "#ffe5ec"
                    : "#eef4ff",
              color:
                status === "done"
                  ? "#08765c"
                  : status === "error"
                    ? colors.red
                    : colors.primary,
              fontWeight: 800,
            }}
          />
        </Stack>

        <LinearProgress
          variant="determinate"
          value={status === "idle" ? 0 : importFlow.progressValue}
          color={status === "error" ? "error" : "primary"}
          sx={{ height: 6 }}
        />

        <Stack spacing={1.2} sx={{ p: 2 }}>
          {validationSteps.map((step) => (
            <Stack
              key={step.id}
              direction="row"
              spacing={1.4}
              alignItems="flex-start"
              sx={{
                p: 1.5,
                borderRadius: 2.2,
                border: `1px solid ${
                  step.status === "active"
                    ? "#b7caff"
                    : step.status === "done"
                      ? "#cfeede"
                      : step.status === "error"
                        ? "#ffc5d1"
                        : colors.border
                }`,
                bgcolor:
                  step.status === "active"
                      ? "var(--ac-highlight-blue, #f3f7ff)"
                    : step.status === "done"
                      ? "var(--ac-highlight-emerald, #f5fcf8)"
                      : step.status === "error"
                        ? "rgba(244, 63, 94, 0.12)"
                        : colors.surfaceAlt,
                transition: "all 0.25s ease",
              }}
            >
              <Box sx={{ pt: 0.15 }}>{renderValidationIndicator(step.status)}</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: colors.text, fontWeight: 800, fontSize: 13.5 }}>
                  {step.title}
                </Typography>
                <Typography sx={{ color: colors.muted, fontSize: 12.5, mt: 0.35, lineHeight: 1.55 }}>
                  {step.detail}
                </Typography>
              </Box>
              <Chip
                label={
                  step.status === "done"
                    ? "OK"
                    : step.status === "active"
                      ? "Em curso"
                      : step.status === "error"
                        ? "Falhou"
                        : "Pendente"
                }
                size="small"
                sx={{
                  alignSelf: "center",
                  bgcolor:
                    step.status === "done"
                      ? "#d8f9e9"
                      : step.status === "active"
                        ? "#eaf1ff"
                        : step.status === "error"
                          ? "#ffe5ec"
                          : "#f1f5f9",
                  color:
                    step.status === "done"
                      ? "#08765c"
                      : step.status === "active"
                        ? colors.primary
                        : step.status === "error"
                          ? colors.red
                          : colors.softMuted,
                  fontWeight: 800,
                }}
              />
            </Stack>
          ))}
        </Stack>

        {importFlow.summary && (status === "processing" || status === "done") ? (
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            sx={{ px: 2, pb: 2 }}
          >
            {importFlow.summary.sheetName ? (
              <Chip
                label={`Aba: ${importFlow.summary.sheetName}`}
                size="small"
                sx={{ bgcolor: colors.surfaceAlt, border: `1px solid ${colors.border}`, fontWeight: 700 }}
              />
            ) : null}
            {importFlow.summary.totalRows ? (
              <Chip
                label={`${importFlow.summary.totalRows} linha(s) lidas`}
                size="small"
                sx={{ bgcolor: colors.surfaceAlt, border: `1px solid ${colors.border}`, fontWeight: 700 }}
              />
            ) : null}
            {importFlow.summary.eligibleRows ? (
              <Chip
                label={`${importFlow.summary.eligibleRows} linha(s) elegiveis`}
                size="small"
                sx={{ bgcolor: colors.surfaceAlt, border: `1px solid ${colors.border}`, fontWeight: 700 }}
              />
            ) : null}
            {status === "done" && typeof importFlow.summary.totalRecords === "number" ? (
              <Chip
                label={`${importFlow.summary.totalRecords} registro(s) refletidos`}
                size="small"
                sx={{ bgcolor: colors.surfaceAlt, border: `1px solid ${colors.border}`, fontWeight: 700 }}
              />
            ) : null}
          </Stack>
        ) : null}
      </Paper>
      ) : null}

      {duplicateMessage ? (
        <Alert severity="warning" variant="outlined">
          {duplicateMessage}
        </Alert>
      ) : null}

      {status === "error" ? (
        <Alert severity="error" variant="outlined">
          {message}
        </Alert>
      ) : null}
    </Stack>
  );
}
